import axios, { AxiosResponse } from 'axios';
import EnvWindow from '../../utils/env';

export enum ValidationResult {
  Valid,
  InvalidAlreadyCoordinator,
  InvalidAlreadyExaminer,
  InvalidAlreadyChairman,
  InvalidAlreadyDomesticCoordinator,
  InvalidAlreadyMemberOrChairman,
}

interface VerifyExaminerResponse {
  examinerNumber: string | null;
  id: string;
  verificationResult: ValidationResult;
}

async function verifyDuplicatePesel(pesel: string): Promise<boolean> {
  const response = await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/Verification/Pesel/${pesel}`);
  return response.data;
}

async function verifyDuplicateMail(mail: string): Promise<boolean> {
  const response = await axios.get(`${EnvWindow.REACT_APP_USERS_API_URL}/Verification/Email/${mail}`);
  return response.data;
}

async function verifyDuplicateExamSessionName(name: string): Promise<boolean> {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS__VERIFICATION_API_URL}/SessionName/${name}`);
  return response.data;
}

async function verifyDuplicateTaskName(examSheetId: string, name: string): Promise<boolean> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__VERIFICATION_API_URL}/ExamSheetId/${examSheetId}/ExamTaskName/${name}`,
  );
  return response.data;
}

async function verifyDuplicateTaskGroupName(examSheetId: string, name: string): Promise<boolean> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__VERIFICATION_API_URL}/examsheet/${examSheetId}/taskgroup/name/${name}`,
  );
  return response.data;
}

async function verifyDuplicateExamSheetName(examId: string, name: string): Promise<boolean> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__VERIFICATION_API_URL}/ExamSheet?examId=${examId}&name=${name}`,
  );
  return response.data;
}

async function verifyDuplicateExamSheetCode(examId: string, code: string): Promise<boolean> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__VERIFICATION_API_URL}/ExamSheet?examId=${examId}&code=${code}`,
  );
  return response.data;
}

async function verifyDuplicateExamName(sessionId: string, name: string): Promise<boolean> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__VERIFICATION_API_URL}/Exam?examSessionid=${sessionId}&name=${name}`,
  );
  return response.data;
}

async function verifyDuplicateExamCode(sessionId: string, code: string): Promise<boolean> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__VERIFICATION_API_URL}/Exam?examSessionid=${sessionId}&code=${code}`,
  );
  return response.data;
}

async function verifyDuplicateSchemaCriterionNumber(gradeRuleId: string, criterionNumber: string): Promise<boolean> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__VERIFICATION_API_URL}/ExamScoringSChema/ExamGradeRule/${gradeRuleId}/SchemaCriterionNumber/${criterionNumber}`,
  );
  return response.data;
}

async function verifyDuplicateSchemaLabel(gradeRuleId: string, schemaLabel: string): Promise<boolean> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__VERIFICATION_API_URL}/ExamScoringSChema/ExamGradeRule/${gradeRuleId}/SchemaLabel/${schemaLabel}`,
  );
  return response.data;
}

async function verifyIfDomesticCoordinatorCanBeAssigned(
  domesticCoordinatorid: string,
  examinerId: string,
): Promise<boolean> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__VERIFICATION_API_URL}/DomesticCoordinator/${domesticCoordinatorid}/isExaminerAssignable/${examinerId}`,
  );
  return response.data;
}

export const verifyTaskGroupCoordinator = async (
  taskGroupCoordinatorId: string,
  examinerId: string,
  assessmentId?: string,
): Promise<AxiosResponse<VerifyExaminerResponse>> => {
  return await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__EXAM_TASK_GROUP_COORINDATOR_API_URL}/${taskGroupCoordinatorId}/Examiner/${examinerId}?assessmentSessionId=${assessmentId}`,
  );
};

export const verifyCZWOexaminer = async (
  groupId: string,
  examinerId: string[],
  assessmentId?: string,
): Promise<AxiosResponse<VerifyExaminerResponse[]>> => {
  const examinerQueryBuild: string = Object.entries(examinerId)
    .map(([, val]) => `examinersId=${val}`)
    .join('&');
  return await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__VERIFICATION_API_URL}/ValidSampleAssessmentGroupMembers/${groupId}?assessmentSessionId=${assessmentId}&${examinerQueryBuild}`,
  );
};

export const verifyZEexaminer = async (
  examiningTeamId: string,
  examinerId: string[],
  assessmentId?: string,
): Promise<AxiosResponse<VerifyExaminerResponse[]>> => {
  return await axios.post(
    `${EnvWindow.REACT_APP_EXAMS__EXAMINING_TEAM_API_URL}/${examiningTeamId}?assessmentSessionId=${assessmentId}`, examinerId
  );
};

export const verifyTeamChairmanCoordinator = async (
  examiningTeamChairmanId: string,
  examinerId: string,
  assessmentId?: string,
): Promise<AxiosResponse<VerifyExaminerResponse>> => {
  return await axios.get(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExaminingTeamChairman/${examiningTeamChairmanId}/Examiner/${examinerId}?assessmentSessionId=${assessmentId}`,
  );
};

export {
  verifyDuplicatePesel,
  verifyDuplicateMail,
  verifyDuplicateExamSessionName,
  verifyDuplicateTaskName,
  verifyDuplicateTaskGroupName,
  verifyDuplicateExamSheetName,
  verifyDuplicateExamSheetCode,
  verifyDuplicateExamName,
  verifyDuplicateExamCode,
  verifyDuplicateSchemaCriterionNumber,
  verifyDuplicateSchemaLabel,
  verifyIfDomesticCoordinatorCanBeAssigned,
};
